import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Trash2,
  Edit2,
  Loader2,
  RefreshCw,
  Wifi,
  WifiOff,
  Clock,
  ArrowDownUp,
  FileText,
  AlertCircle,
  Network,
} from 'lucide-react';
import api from '../../services/api';
import { useTenantStore } from '../../stores/tenantStore';
import WireGuardPeerModal from './WireGuardPeerModal';
import WireGuardExport from './WireGuardExport';
import ConfirmDialog from '../common/ConfirmDialog';
import { formatDateOnly } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { usePermissions } from '../../hooks/usePermissions';

interface WireGuardPeer {
  id: string;
  name: string;
  description?: string;
  publicKey: string;
  assignedIp: string;
  allowedIps: string[];
  persistentKeepalive: number;
  enabled: boolean;
  endpoint?: string;
  status?: 'pending' | 'active' | 'inactive' | 'deleted' | 'connected' | 'disconnected';
  lastHandshake?: string;
  rxBytes?: number;
  txBytes?: number;
  peerEndpoint?: string;
  statusUpdatedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  isConnector?: boolean;
  connectorId?: string;
}

interface WireGuardPeersProps {
  edgeId: string;
  configured: boolean;
  serverNetwork?: string;
}

const WireGuardPeers = ({ edgeId, configured, serverNetwork }: WireGuardPeersProps) => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [peers, setPeers] = useState<WireGuardPeer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editPeer, setEditPeer] = useState<WireGuardPeer | null>(null);
  const [deletePeer, setDeletePeer] = useState<WireGuardPeer | null>(null);
  const [exportPeer, setExportPeer] = useState<WireGuardPeer | null>(null);

  const fetchPeers = async () => {
    if (!configured) {
      setPeers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v2/edges/${edgeId}/wireguard/peers`);
      setPeers(response.data?.items || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load peers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPeers();

    // Set up real-time status polling (every 10 seconds)
    if (!configured) return;

    const interval = setInterval(async () => {
      try {
        const statusRes = await api.get(`/api/v2/edges/${edgeId}/wireguard/status`);
        if (statusRes.data?.peers) {
          // Merge live status with existing peer list
          setPeers((prevPeers) =>
            prevPeers.map((peer) => {
              const liveStatus = statusRes.data.peers.find((p: any) => p.id === peer.id);
              if (liveStatus) {
                return {
                  ...peer,
                  status: liveStatus.status || (liveStatus.connected ? 'connected' : 'disconnected'),
                  lastHandshake: liveStatus.lastHandshake,
                  rxBytes: liveStatus.rxBytes || 0,
                  txBytes: liveStatus.txBytes || 0,
                };
              }
              return peer;
            })
          );
        }
      } catch (err) {
        console.error('Failed to poll peer status:', err);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [edgeId, configured, selectedTenantId]);

  const handleAddPeer = async (data: any) => {
    const response = await api.post(`/api/v2/edges/${edgeId}/wireguard/peers`, data);
    await fetchPeers();
    return {
      presharedKey: response.data?.presharedKey,
      assignedIp: response.data?.assignedIp,
    };
  };

  const handleEditPeer = async (data: any) => {
    if (!editPeer) return;
    await api.put(`/api/v2/edges/${edgeId}/wireguard/peers/${editPeer.id}`, data);
    await fetchPeers();
    setEditPeer(null);
  };

  const handleDeletePeer = async () => {
    if (!deletePeer) return;
    try {
      await api.delete(`/api/v2/edges/${edgeId}/wireguard/peers/${deletePeer.id}`);
      await fetchPeers();
      setDeletePeer(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete peer');
    }
  };

  const formatBytes = (bytes?: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  const formatLastHandshake = (timestamp?: string): string => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return formatDateOnly(date, dateSettings);
  };

  const getStatusBadge = (peer: WireGuardPeer) => {
    if (!peer.enabled) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Disabled
        </span>
      );
    }

    switch (peer.status) {
      case 'connected':
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Wifi className="w-3 h-3 mr-1" />
            Connected
          </span>
        );
      case 'disconnected':
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <WifiOff className="w-3 h-3 mr-1" />
            Inactive
          </span>
        );
      case 'deleted':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            Deleting...
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  if (!configured) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
        <Users className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-500">
          Enable WireGuard server first to manage IoT gateway peers
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">IoT Gateway Peers</h3>
          <span className="text-sm text-gray-500">({peers.length})</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchPeers}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-1 px-3 py-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {canCreate && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary inline-flex items-center space-x-1 px-3 py-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>Add Gateway</span>
          </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Peers Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Gateway
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Assigned IP
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Endpoint
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Last Handshake
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Transfer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                  Loading peers...
                </td>
              </tr>
            ) : peers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p>No IoT gateway peers configured</p>
                  {canCreate && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Add your first gateway →
                  </button>
                  )}
                </td>
              </tr>
            ) : (
              peers.map((peer) => (
                <tr key={peer.id} className={`hover:bg-gray-50 ${peer.isConnector ? 'bg-blue-50/30' : ''}`}>
                  <td className="px-4 py-3">
                    <div>
                      <div className="flex items-center space-x-2">
                        {peer.isConnector && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                            <Network className="w-3 h-3 mr-0.5" />
                            Connector
                          </span>
                        )}
                        {peer.isConnector && peer.connectorId ? (
                          <button
                            onClick={() => navigate(`/connectors/${peer.connectorId}`)}
                            className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
                          >
                            {peer.name.replace(/^\[Connector\]\s*/, '')}
                          </button>
                        ) : (
                          <span className="text-sm font-medium text-gray-900">{peer.name}</span>
                        )}
                      </div>
                      {peer.description && (
                        <div className="text-xs text-gray-500">{peer.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                    {peer.assignedIp}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                    {peer.isConnector
                      ? <span className="text-gray-400 italic">Dynamic</span>
                      : (peer.endpoint || <span className="text-gray-400 italic">Not set</span>)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{getStatusBadge(peer)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatLastHandshake(peer.lastHandshake)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <ArrowDownUp className="w-3 h-3" />
                      <span>
                        ↓{formatBytes(peer.rxBytes)} ↑{formatBytes(peer.txBytes)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {peer.isConnector ? (
                      <button
                        onClick={() => navigate(`/connectors/${peer.connectorId}`)}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        View Connector
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setExportPeer(peer)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Export config"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        {canUpdate && (
                        <button
                          onClick={() => setEditPeer(peer)}
                          className="text-gray-600 hover:text-gray-800 p-1"
                          title="Edit peer"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        )}
                        {canDelete && (
                        <button
                          onClick={() => setDeletePeer(peer)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete peer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      <WireGuardPeerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddPeer}
        serverNetwork={serverNetwork}
      />

      <WireGuardPeerModal
        isOpen={!!editPeer}
        onClose={() => setEditPeer(null)}
        onSave={handleEditPeer}
        peer={editPeer || undefined}
        serverNetwork={serverNetwork}
      />

      <WireGuardExport
        isOpen={!!exportPeer}
        onClose={() => setExportPeer(null)}
        edgeId={edgeId}
        peer={exportPeer}
      />

      <ConfirmDialog
        isOpen={!!deletePeer}
        onClose={() => setDeletePeer(null)}
        onConfirm={handleDeletePeer}
        title="Delete IoT Gateway"
        message={`Are you sure you want to remove "${deletePeer?.name}"? This will disconnect the gateway immediately.`}
        confirmText="Delete Gateway"
        variant="danger"
      />
    </div>
  );
};

export default WireGuardPeers;
